import type { AuthContext, Position } from '../types/index.ts';
import { retry } from '../lib/retry.ts';
import { signSettlePnl, signWithdraw } from './wallet/eip712.ts';
import type { Eip712Signer } from './wallet/eip712.ts';
import { signSolanaSettlePnl, signSolanaWithdraw } from './wallet/solana.ts';
import type { SolanaMessageSigner } from './wallet/solana.ts';
import * as api from './orderly-api.ts';
import { parseAtomicWithdrawalAmount } from '../lib/withdrawal.ts';

type WalletActionSigner = Eip712Signer | SolanaMessageSigner;
type WalletChainType = 'evm' | 'solana';

interface Callbacks {
  onProgress?: (msg: string) => void;
  shouldContinue?: () => boolean;
}

export class OffboardingOperationAbortedError extends Error {
  constructor() {
    super('Offboarding operation stopped because the session changed');
    this.name = 'OffboardingOperationAbortedError';
  }
}

function ensureShouldContinue(cb?: Callbacks): void {
  if (cb?.shouldContinue?.() === false) {
    throw new OffboardingOperationAbortedError();
  }
}

export interface ConvertToUsdcResult {
  settled: boolean;
  covered: boolean;
  usdcBalance: number;
  collateralToken: string;
  collateralHolding: number;
}

interface ConvertPollOptions {
  attempts?: number;
  delayMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

interface ClosePositionsPollOptions {
  attempts?: number;
  delayMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

export async function executeCancelOrders(auth: AuthContext, accountId: string, cb?: Callbacks): Promise<void> {
  ensureShouldContinue(cb);
  cb?.onProgress?.('Cancelling all orders...');
  await Promise.allSettled([
    (async () => {
      ensureShouldContinue(cb);
      await api.cancelAllOrders(auth, undefined, accountId);
    })(),
    (async () => {
      ensureShouldContinue(cb);
      await api.cancelAllAlgoOrders(auth, accountId);
    })(),
  ]);

  if (cb?.shouldContinue?.() === false) return;
  cb?.onProgress?.('Verifying cancellation...');
  await retry(async () => {
    if (cb?.shouldContinue?.() === false) return;
    let remaining;
    let remainingAlgo;
    try {
      [remaining, remainingAlgo] = await Promise.all([
        api.getOpenOrders(auth, accountId),
        api.getOpenAlgoOrders(auth, accountId),
      ]);
    } catch {
      if (cb?.shouldContinue?.() === false) return;
      throw new Error('Unable to verify order cancellation. Please try again.');
    }
    if (cb?.shouldContinue?.() === false) return;

    const standardCount = remaining.rows?.length ?? 0;
    const conditionalCount = remainingAlgo.rows?.length ?? 0;
    const openOrders = [
      standardCount > 0 ? `${standardCount} standard order${standardCount === 1 ? '' : 's'}` : undefined,
      conditionalCount > 0 ? `${conditionalCount} conditional order${conditionalCount === 1 ? '' : 's'}` : undefined,
    ].filter(Boolean);
    if (openOrders.length > 0) {
      throw new Error(`${openOrders.join(' and ')} still open after cancellation. Please retry.`);
    }
  }, { retries: 4, delayMs: 750 });
  cb?.onProgress?.('All orders cancelled.');
}

export async function executeClosePositions(
  positions: Position[],
  auth: AuthContext,
  cb?: Callbacks,
  poll: ClosePositionsPollOptions = {},
): Promise<void> {
  const targetKeys = new Set(positions.map((pos) => `${pos.accountId}:${pos.symbol}:${pos.marginMode}`));
  const targetAccountIds = [...new Set(positions.map((pos) => pos.accountId))];
  const attempts = poll.attempts ?? 5;
  const delayMs = poll.delayMs ?? 750;
  const sleep = poll.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const readTargets = async (accountIds: Iterable<string>) => {
    const snapshots = await Promise.all([...accountIds].map(async (accountId) => {
      const response = await api.getPositions(auth, accountId);
      if (!Array.isArray(response.rows)) throw new Error('Invalid positions response');
      return { accountId, rows: response.rows };
    }));
    ensureShouldContinue(cb);
    return snapshots.flatMap(({ accountId, rows }) => rows
      .filter((pos) => targetKeys.has(`${accountId}:${pos.symbol}:${pos.margin_mode}`) && Math.abs(pos.position_qty) > 0)
      .map((pos) => ({ ...pos, accountId })));
  };

  cb?.onProgress?.('Checking current positions...');
  let currentPositions;
  ensureShouldContinue(cb);
  try {
    currentPositions = await readTargets(targetAccountIds);
  } catch (error) {
    if (error instanceof OffboardingOperationAbortedError) throw error;
    throw new Error('Unable to read current positions. No close orders were submitted.');
  }

  for (const pos of currentPositions) {
    ensureShouldContinue(cb);
    cb?.onProgress?.(`Closing ${pos.symbol} ${pos.position_qty > 0 ? 'Long' : 'Short'} ${pos.margin_mode}...`);
    try {
      await api.placeOrder({
        symbol: pos.symbol,
        order_type: 'MARKET',
        side: pos.position_qty > 0 ? 'SELL' : 'BUY',
        order_quantity: Math.abs(pos.position_qty),
        reduce_only: true,
        margin_mode: pos.margin_mode,
      }, auth, pos.accountId);
    } catch {
      ensureShouldContinue(cb);
    }
  }

  ensureShouldContinue(cb);
  cb?.onProgress?.('Verifying position closure...');
  let remaining = currentPositions;
  let pendingAccountIds = new Set(currentPositions.map((pos) => pos.accountId));
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      remaining = await readTargets(pendingAccountIds);
    } catch (error) {
      if (error instanceof OffboardingOperationAbortedError) throw error;
      throw new Error('Unable to verify position closure. Please retry.');
    }
    if (remaining.length === 0) {
      cb?.onProgress?.('All positions closed.');
      return;
    }
    pendingAccountIds = new Set(remaining.map((pos) => pos.accountId));
    if (attempt < attempts - 1) {
      await sleep(delayMs);
      ensureShouldContinue(cb);
    }
  }

  const quantities = remaining
    .map((pos) => `${pos.symbol} ${pos.margin_mode}: ${Math.abs(pos.position_qty)}`)
    .join(', ');
  throw new Error(`Positions still open after close attempt: ${quantities}. Please retry.`);
}

export async function executeSettlePnl(
  accountType: 'main' | 'sub',
  accountId: string,
  brokerId: string,
  auth: AuthContext,
  signer: WalletActionSigner,
  chainId: number,
  chainType: WalletChainType,
  cb?: Callbacks,
): Promise<void> {
  cb?.onProgress?.('Getting settle nonce...');
  const { settle_nonce } = await api.getSettleNonce(auth, accountType === 'sub' ? accountId : undefined);

  if (accountType === 'sub') {
    ensureShouldContinue(cb);
    cb?.onProgress?.('Settling sub-account PnL...');
    await api.subAccountSettlePnl(settle_nonce, accountId, auth);
  } else {
    ensureShouldContinue(cb);
    cb?.onProgress?.('Requesting wallet signature for settlement...');
    const timestamp = Date.now();
    const message = { brokerId, chainId, settleNonce: settle_nonce, timestamp };
    const signature = chainType === 'solana'
      ? await signSolanaSettlePnl(signer as SolanaMessageSigner, message)
      : await signSettlePnl(signer as Eip712Signer, message);
    ensureShouldContinue(cb);
    const userAddress = await signer.getAddress();
    ensureShouldContinue(cb);
    cb?.onProgress?.('Submitting settlement...');
    await api.settlePnl({ message: { ...message, chainType: chainType === 'solana' ? 'SOL' : 'EVM' }, signature, userAddress }, auth);
  }
  cb?.onProgress?.('PnL settled.');
}

export async function executeConvertToUsdc(
  accountId: string,
  plan: Array<{ token: string; amount: number }>,
  auth: AuthContext,
  cb?: Callbacks,
  slippage = 0.01,
  poll: ConvertPollOptions = {},
): Promise<ConvertToUsdcResult> {
  if (plan.length !== 1) {
    throw new Error('Select one collateral asset to convert');
  }

  const item = plan[0];
  const attempts = poll.attempts ?? 5;
  const delayMs = poll.delayMs ?? 1500;
  const sleep = poll.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  cb?.onProgress?.('Checking current balances...');
  const baselineHoldings = await api.getHolding(auth, accountId);
  ensureShouldContinue(cb);
  const baselineRows = baselineHoldings.rows ?? [];
  const baselineUsdcRow = baselineRows.find((row) => row.token === 'USDC');
  const baselineCollateralRow = baselineRows.find((row) => row.token === item.token);
  const hasBaseline = baselineUsdcRow
    && baselineCollateralRow
    && Number.isFinite(baselineUsdcRow.holding)
    && Number.isFinite(baselineUsdcRow.updated_time)
    && Number.isFinite(baselineCollateralRow.holding)
    && Number.isFinite(baselineCollateralRow.updated_time);
  if (!hasBaseline) {
    throw new Error('Unable to establish current USDC and collateral balances. Refresh account data and try again.');
  }
  const baselineUsdc = baselineUsdcRow.holding;
  const baselineCollateral = baselineCollateralRow.holding;
  const baselineCollateralUpdatedTime = baselineCollateralRow.updated_time;

  ensureShouldContinue(cb);
  cb?.onProgress?.(`Converting ${item.token} to USDC...`);
  await api.manualConvert({
    converted_asset: item.token,
    amount: item.amount,
    slippage,
  }, auth, accountId);

  cb?.onProgress?.('Confirming settlement...');

  let usdcHolding = baselineUsdc;
  let collateralHolding = baselineCollateral;
  let settled = false;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const holdings = await api.getHolding(auth, accountId);
    ensureShouldContinue(cb);
    const rows = holdings.rows ?? [];
    const usdcRow = rows.find((row) => row.token === 'USDC');
    const collateralRow = rows.find((row) => row.token === item.token);

    if (collateralRow && Number.isFinite(collateralRow.holding)) {
      collateralHolding = collateralRow.holding;
    }

    const tolerance = Number.EPSILON * Math.max(
      1,
      Math.abs(baselineCollateral),
      Math.abs(collateralHolding),
      Math.abs(item.amount),
    ) * 8;
    const snapshotFresh = rows.length > 0 && rows.some(
      (row) => Number.isFinite(row.updated_time) && row.updated_time > baselineCollateralUpdatedTime,
    );
    const sourceFresh = collateralRow
      ? Number.isFinite(collateralRow.updated_time)
        && collateralRow.updated_time > baselineCollateralUpdatedTime
      : snapshotFresh;
    const sourceHolding = collateralRow ? collateralRow.holding : 0;
    const sourceDebitApplied = sourceFresh
      && Number.isFinite(sourceHolding)
      && baselineCollateral - sourceHolding + tolerance >= item.amount;
    const usdcImproved = usdcRow
      && Number.isFinite(usdcRow.holding)
      && usdcRow.holding > baselineUsdc;

    if (sourceDebitApplied && (usdcImproved || !usdcRow)) {
      usdcHolding = usdcRow?.holding ?? 0;
      collateralHolding = sourceHolding;
      settled = true;
      break;
    }

    if (usdcRow && Number.isFinite(usdcRow.holding)) {
      usdcHolding = usdcRow.holding;
    }

    if (attempt < attempts - 1) {
      await sleep(delayMs);
      ensureShouldContinue(cb);
    }
  }

  if (!settled) {
    cb?.onProgress?.('Conversion submitted. Balance is still settling.');
    return { settled: false, covered: false, usdcBalance: usdcHolding, collateralToken: item.token, collateralHolding };
  }

  const covered = usdcHolding >= 0;
  cb?.onProgress?.(covered ? 'USDC balance covered.' : `USDC still needs ${Math.abs(usdcHolding)} USDC covered.`);

  return {
    settled: true,
    covered,
    usdcBalance: usdcHolding,
    collateralToken: item.token,
    collateralHolding,
  };
}

export async function executeInternalTransfer(
  fromAccountId: string,
  toAccountId: string,
  token: string,
  amount: number,
  auth: AuthContext,
  cb?: Callbacks,
): Promise<void> {
  ensureShouldContinue(cb);
  cb?.onProgress?.(`Transferring ${token} to target account...`);
  await api.internalTransfer({
    token,
    amount,
    from_account_id: fromAccountId,
    to_account_id: toAccountId,
  }, auth);
  cb?.onProgress?.('Funds transferred.');
}

export async function executeWithdrawOrTransfer(
  account: { type: 'main' | 'sub'; accountId: string },
  token: string,
  rawAmount: string,
  mainAccountId: string,
  brokerId: string,
  auth: AuthContext,
  signer: WalletActionSigner | undefined,
  chainId: number | undefined,
  chainType: WalletChainType | undefined,
  destinationChainId: number | undefined,
  receiverAddress: string | undefined,
  cb: Callbacks | undefined,
  tokenDecimals: number | undefined,
): Promise<void> {
  if (account.type === 'sub') {
    if (!mainAccountId) {
      throw new Error('Main account ID is required for sub-account transfer');
    }
    ensureShouldContinue(cb);
    cb?.onProgress?.(`Transferring ${token} to main account...`);
      await api.internalTransfer({
        token,
        amount: Number(rawAmount),
        from_account_id: account.accountId,
        to_account_id: mainAccountId,
      }, auth);
    cb?.onProgress?.('Funds transferred.');
    return;
  } else {
    if (!signer || !chainId || !chainType) {
      throw new Error('Network not detected for withdrawal');
    }
    const { atomicAmount } = parseAtomicWithdrawalAmount(rawAmount, tokenDecimals as number);
    cb?.onProgress?.('Getting withdraw nonce...');
    const { withdraw_nonce } = await api.getWithdrawNonce(auth);
    ensureShouldContinue(cb);
    const userAddress = await signer.getAddress();
    const receiver = receiverAddress ?? userAddress;
    const timestamp = Date.now();

    const withdrawChainId = destinationChainId ?? chainId;

    const withdrawMessage = {
      brokerId,
      chainId: withdrawChainId,
      receiver,
      token,
      amount: atomicAmount,
      withdrawNonce: withdraw_nonce,
      timestamp,
      allowCrossChainWithdraw: false as const,
    };
    ensureShouldContinue(cb);
    cb?.onProgress?.(`Requesting wallet signature to withdraw ${token}...`);
    const signature = chainType === 'solana'
      ? await signSolanaWithdraw(signer as SolanaMessageSigner, withdrawMessage)
      : await signWithdraw(signer as Eip712Signer, withdrawMessage);
    ensureShouldContinue(cb);
    cb?.onProgress?.('Submitting withdrawal...');
    await api.withdrawRequest({
      message: {
        ...withdrawMessage,
        amount: withdrawMessage.amount.toString(),
        chainType: chainType === 'solana' ? 'SOL' : 'EVM',
      },
      signature,
      userAddress,
    }, auth);
    cb?.onProgress?.('Withdrawal submitted.');
  }
}
